import { useEffect, useRef, useCallback } from 'react';

// Channel registry: one BroadcastChannel per channelName + many subscribers
const REGISTRY = new Map(); // channelName -> { channel, subscribers: Set<fn> }

function getOrCreateEntry(channelName) {
  let entry = REGISTRY.get(channelName);
  if (!entry) {
    const ch = new BroadcastChannel(channelName);
    const subscribers = new Set();
    ch.onmessage = (event) => {
      const msg = event.data || {};
      // fan out to all active subscribers in this tab
      subscribers.forEach((fn) => {
        try { fn(msg, event); } catch (e) { /* optional: console.error(e) */ }
      });
    };
    entry = { channel: ch, subscribers };
    REGISTRY.set(channelName, entry);
  }
  return entry;
}

/**
 * useBroadcastChannel
 * - Message shape: { id, from, to?, type, payload }
 * - handlers: { [type]: (payload, event, msg) => void, onMessage?: fn, "*": fn }
 */
export function useBroadcastChannel(
  channelName,
  handlers = {},
  { ignoreSelf = true } = {}
) {
  const tabIdRef = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`
  );
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; }, [handlers]);

  // subscribe this component
  useEffect(() => {
    const entry = getOrCreateEntry(channelName);
    const processedIds = new Set();

    const listener = (msg, event) => {
      const { id, from, to, type, payload } = msg || {};

      // de-dupe and routing
      if (id && processedIds.has(id)) return;
      if (ignoreSelf && from === tabIdRef.current) return;
      if (to && to !== tabIdRef.current) return;

      if (id) {
        processedIds.add(id);
        if (processedIds.size > 5000) processedIds.clear();
      }

      const fn =
        handlersRef.current?.[type] ||
        handlersRef.current?.['*'] ||
        handlersRef.current?.onMessage;

      if (typeof fn === 'function') fn(payload, event, msg);
    };

    entry.subscribers.add(listener);

    return () => {
      entry.subscribers.delete(listener);
      if (entry.subscribers.size === 0) {
        // last subscriber gone: close and remove channel
        entry.channel.close();
        REGISTRY.delete(channelName);
      }
    };
  }, [channelName, ignoreSelf]);

  const sendMessage = useCallback((type, payload, { to } = {}) => {
    const entry = getOrCreateEntry(channelName);
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    entry.channel.postMessage({ id, from: tabIdRef.current, to, type, payload });
  }, [channelName]);

  return { sendMessage, tabId: tabIdRef.current };
}
